'use client';

import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Zap, User, LogOut, Menu, X, Github, Sun, Moon, History, ArrowRight, Globe } from 'lucide-react';

export function Layout() {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const isActive = (path: string) => location.pathname === path;

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Premium Navigation */}
            <nav className="sticky top-0 z-50 bg-card/80 backdrop-blur-md border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-14">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary">
                                <Zap className="h-5 w-5 text-primary-foreground" />
                            </div>
                            <span className="text-lg font-bold text-foreground">BgRemover</span>
                        </Link>

                        {/* Desktop Menu */}
                        <div className="hidden md:flex items-center gap-8">
                            <Link
                                to="/"
                                className={`text-sm font-medium transition-colors ${isActive('/')
                                    ? 'text-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                Home
                            </Link>
                            <Link
                                to="/pricing"
                                className={`text-sm font-medium transition-colors ${isActive('/pricing')
                                    ? 'text-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                Pricing
                            </Link>
                            <Link
                                to="/developer"
                                className={`text-sm font-medium transition-colors ${isActive('/developer')
                                    ? 'text-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                Developer API
                            </Link>

                            <Link
                                to="/services"
                                className={`text-sm font-medium transition-colors ${isActive('/services')
                                    ? 'text-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                Services
                            </Link>
                            <Link
                                to="/about"
                                className={`text-sm font-medium transition-colors ${isActive('/about')
                                    ? 'text-primary'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                About Us
                            </Link>
                        </div>

                        {/* Right Side */}
                        <div className="flex items-center gap-4">
                            {/* Theme Toggle */}
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-lg hover:bg-background transition-colors text-muted-foreground hover:text-foreground"
                                aria-label="Toggle theme"
                            >
                                {theme === 'light' ? (
                                    <Moon className="h-5 w-5" />
                                ) : (
                                    <Sun className="h-5 w-5" />
                                )}
                            </button>

                            {user ? (
                                <div className="hidden sm:flex items-center gap-4">
                                    <Link
                                        to="/history"
                                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <History className="h-4 w-4" />
                                        History
                                    </Link>
                                    <Link
                                        to="/dashboard"
                                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <User className="h-4 w-4" />
                                        Dashboard
                                    </Link>
                                    <button
                                        onClick={logout}
                                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <LogOut className="h-4 w-4" />
                                        Logout
                                    </button>
                                </div>
                            ) : (
                                <div className="hidden sm:flex items-center gap-3">
                                    <Link
                                        to="/login"
                                        className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        Log in
                                    </Link>
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                                    >
                                        Sign up
                                    </Link>
                                </div>
                            )}

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-background transition-colors"
                            >
                                {mobileMenuOpen ? (
                                    <X className="h-5 w-5 text-foreground" />
                                ) : (
                                    <Menu className="h-5 w-5 text-foreground" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Menu */}
                    {mobileMenuOpen && (
                        <div className="md:hidden pb-4 border-t border-border">
                            <Link
                                to="/"
                                className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                            >
                                Home
                            </Link>
                            <Link
                                to="/pricing"
                                className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                            >
                                Pricing
                            </Link>
                            <Link
                                to="/developer"
                                className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                            >
                                Developer API
                            </Link>
                            <Link
                                to="/services"
                                className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                            >
                                Services
                            </Link>
                            <Link
                                to="/about"
                                className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                            >
                                About Us
                            </Link>

                            {user ? (
                                <>
                                    <Link
                                        to="/history"
                                        className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                                    >
                                        History
                                    </Link>
                                    <Link
                                        to="/dashboard"
                                        className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                                    >
                                        Dashboard
                                    </Link>
                                    <button
                                        onClick={logout}
                                        className="block w-full text-left px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                                    >
                                        Logout
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary"
                                    >
                                        Log in
                                    </Link>
                                    <Link
                                        to="/signup"
                                        className="block px-4 py-2 text-sm font-medium text-primary"
                                    >
                                        Sign up
                                    </Link>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </nav>

            {/* Main Content */}
            <main className="flex-grow">
                <Outlet />
            </main>

            {/* Premium Footer */}
            <footer className="border-t border-border mt-auto bg-card/50 backdrop-blur-sm relative overflow-hidden">
                {/* Decorative gradient */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

                <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 border border-primary/20">
                                    <Zap className="h-5 w-5 text-primary" />
                                </div>
                                <span className="font-bold text-xl text-foreground">BgRemover</span>
                            </div>
                            <p className="text-muted-foreground leading-relaxed">
                                Professional grade background removal powered by advanced AI. fast, accurate, and secure.
                            </p>
                        </div>

                        <div>
                            <h3 className="font-semibold text-foreground mb-6">Product</h3>
                            <ul className="space-y-4">
                                <li>
                                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                                        <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        Editor
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                                        Pricing
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/history" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                                        History
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold text-foreground mb-6">Resources</h3>
                            <ul className="space-y-4">
                                <li>
                                    <Link to="/services" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                                        Services
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                                        About Us
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/developer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                                        API Documentation
                                    </Link>
                                </li>
                                {/* Removed Status Page */}
                                <li>
                                    <a href="https://barcodely.cloud" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2">
                                        Barcodely.cloud
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold text-foreground mb-6">Legal</h3>
                            <ul className="space-y-4">
                                <li>
                                    <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
                                        Privacy Policy
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                                        Terms of Service
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/cookies" className="text-muted-foreground hover:text-primary transition-colors">
                                        Cookie Policy
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="font-semibold text-foreground mb-6">Contact</h3>
                            <ul className="space-y-4 text-sm text-muted-foreground">
                                {/* Removed physical address per user request */}
                                <li>
                                    <a href="mailto:sagarmiishra8169@gmail.com" className="hover:text-primary transition-colors">
                                        sagarmiishra8169@gmail.com
                                    </a>
                                </li>
                                <li>
                                    <a href="https://wa.me/918169312050" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                                        +918169312050 (WhatsApp Only)
                                    </a>
                                </li>
                                <li>
                                    <a href="https://bgdrop.cloud" className="hover:text-primary transition-colors">
                                        bgdrop.cloud
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-muted-foreground">
                            &copy; {new Date().getFullYear()} BgRemover. All rights reserved.
                        </p>
                        <div className="flex items-center gap-6">
                            {/* Add more social icons here if needed */}
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
